import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DateTime } from 'luxon'
import { centsToDollars } from '@/utils/currencyHelpers'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'
import { useReselector } from '@/utils/sharedHooks'
import { updateUserTransaction } from '@/features/transactions/transactions.slice'

import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'

import {
  getAllTransactionCategories,
  selectCategoriesByType,
  selectNonExpenseAndIncomeCategories,
} from '@/features/reports/reports.selectors'
import { SampleTransactionNotesModal } from './SampleTransactionNotesModal'
import { Button, Card, CardColors, Text, Select } from '@/ui'

export const SampleTransactionCard = ({
  transaction,
  displaySampleTransaction,
  updateSampleTransaction,
}: {
  transaction: Transaction
  displaySampleTransaction: boolean
  updateSampleTransaction: (
    transactionId: number,
    updates: Partial<Transaction>
  ) => void
}) => {
  const dispatch = useAppDispatch()
  const [modalOpen, setModalOpen] = useState(false)
  const [transactionType, setTransactionType] = useState<
    TransactionRuleCategoryType | undefined
  >()
  const [transactionCategory, setTransactionCategory] = useState<string>('')
  const [transactionNote, setTransactionNote] = useState<string | undefined>()
  const allTransactionCategories = useReselector(getAllTransactionCategories)
  const expenseCategories = useReselector(selectCategoriesByType, 'Expenses')
  const incomeCategories = useReselector(selectCategoriesByType, 'Income')
  const otherCategories = useReselector(selectNonExpenseAndIncomeCategories)
  const transactionAmount = centsToDollars(transaction.amountInCents)

  const categoriesToDisplay = useMemo(() => {
    if (transactionAmount) {
      if (transactionAmount < 0) {
        return { ...expenseCategories, ...otherCategories }
      }
      if (transactionAmount > 0) {
        return { ...incomeCategories, ...otherCategories }
      }
    }
    return allTransactionCategories
  }, [
    transactionAmount,
    expenseCategories,
    incomeCategories,
    otherCategories,
    allTransactionCategories,
  ])

  const orderedTransactionCategoriesToDisplay = Object.values(
    categoriesToDisplay
  ).sort((a, b) => a.name.localeCompare(b.name))

  const orderedCategoryValuesToDisplay = Object.values(
    orderedTransactionCategoriesToDisplay
  ).map((category) => ({
    text: category.name,
    value: category.name,
  }))

  useEffect(() => {
    if (
      transaction.type === TransactionRuleCategoryType.business ||
      transaction.type === TransactionRuleCategoryType.personal
    ) {
      setTransactionType(transaction.type)
    } else {
      setTransactionType(undefined)
    }

    setTransactionCategory(transaction.transactionCategory?.name || '')
    setTransactionNote(transaction.notes || '')
  }, [transaction])

  const updateTransactionCategory = (value: string | undefined) => {
    if (!value) return

    setTransactionCategory(value)
    const categoryToUpdate = Object.values(allTransactionCategories).find(
      (category) => category.name === value
    )

    if (displaySampleTransaction) {
      updateSampleTransaction(transaction.id, {
        transactionCategory: categoryToUpdate,
        transactionCategoryId: categoryToUpdate?.id,
        type: categoryToUpdate
          ? TransactionRuleCategoryType.business
          : undefined,
      })
    } else {
      updateUserTransaction(transaction.id, {
        transactionCategoryId: categoryToUpdate?.id || null,
        // Auto set the type as business if categorized
        type: categoryToUpdate
          ? TransactionRuleCategoryType.business
          : undefined,
      })(dispatch)
    }
  }

  const updateTransactionType = (
    value: TransactionRuleCategoryType | undefined
  ) => {
    if (!value) return

    setTransactionType(value)

    if (displaySampleTransaction) {
      updateSampleTransaction(transaction.id, {
        type: value,
        // Clear category if transaction is personal
        transactionCategory:
          value === 'personal' ? null : transaction.transactionCategory,
        transactionCategoryId:
          value === 'personal' ? null : transaction.transactionCategoryId,
      })
    } else {
      updateUserTransaction(transaction.id, {
        type: value,
        // Clear category if transaction is personal
        transactionCategoryId: value === 'personal' ? null : undefined,
      })(dispatch)
    }
  }

  const saveNote = async () => {
    setModalOpen(false)

    if (displaySampleTransaction) {
      updateSampleTransaction(transaction.id, {
        notes: transactionNote,
        notesLastUpdatedAt: new Date().toISOString(),
      })
    } else {
      await updateUserTransaction(transaction.id, {
        notes: transactionNote,
        notesLastUpdatedAt: DateTime.now().toISO(),
      })(dispatch)
    }
  }

  return (
    <Card color={CardColors.LEVEL_1} className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:space-x-4 flex-wrap">
        <Text className="w-full md:w-1/12">
          {isoToUTCDateTime(transaction.date).toFormat(
            DATE_FORMATS_LUXON.DISPLAY_SHORT_YEAR
          )}
        </Text>
        <Text className="w-full md:w-1/6">{transaction.description}</Text>
        <Text className="w-full md:w-1/12">
          <CurrencyFormatLabel value={transactionAmount} />
        </Text>
        <div className="w-full md:w-1/6">
          <Select
            options={[
              {
                text: 'Business',
                value: TransactionRuleCategoryType.business,
              },
              {
                text: 'Personal',
                value: TransactionRuleCategoryType.personal,
              },
            ]}
            value={transactionType}
            onValueChange={updateTransactionType}
            variant="text"
            placeholder="Select Type"
          />
        </div>
        <div className="w-full md:w-1/3">
          {transaction.type === 'personal' ? (
            <Text>
              <b>N/A</b>
            </Text>
          ) : (
            <Select
              options={orderedCategoryValuesToDisplay}
              value={transactionCategory}
              onValueChange={updateTransactionCategory}
              placeholder="Select Category"
              searchable
            />
          )}
        </div>
        <div className="w-full md:w-1/12 text-right">
          <Button onClick={() => setModalOpen(true)} variant="link">
            {transactionNote ? 'Edit Note' : 'Add Note'}
          </Button>
          <SampleTransactionNotesModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            transaction={transaction}
            transactionNote={transactionNote}
            onNoteChange={setTransactionNote}
            onSave={saveNote}
          />
        </div>
      </div>
    </Card>
  )
}
